import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useMemo,
} from "react";
import DefaultModal, { LoadingModal } from "components/modals/ModalTemplate";
import { DefaultModalProps, ModalType } from "types/Modal";

interface GlobalModalContextType {
    showModal: (
        props: Partial<DefaultModalProps> & { modalType: ModalType }
    ) => void;
    setModalShow: (show: boolean) => void;
    showLoadingModal: (title?: string) => void;
    hideLoadingModal: () => void;
    isModalVisible: boolean;
    isLoadingModalVisible: boolean;
}

const GlobalModalContext = createContext<GlobalModalContextType | undefined>(
    undefined
);

interface GlobalModalProviderProps {
    children: ReactNode;
}

export const GlobalModalProvider: React.FC<GlobalModalProviderProps> = ({
    children,
}) => {
    const [modalShow, setModalShow] = useState<boolean>(false);
    const [loadingModalShow, setLoadingModalShow] = useState<boolean>(false);
    const [modalProps, setModalProps] = useState<DefaultModalProps>({
        modalShow: false,
        setModalShow,
    });
    const [loadingModalTitle, setLoadingModalTitle] = useState<string>("");

    const showModal = (
        props: Partial<DefaultModalProps> & { modalType: ModalType }
    ) => {
        setModalProps({
            ...props,
            modalShow: false, // This will be overridden by the modalShow prop in the render
            setModalShow,
        });
        setModalShow(true);
    };

    const showLoadingModal = (title: string = "") => {
        setLoadingModalTitle(title);
        setLoadingModalShow(true);
    };

    const hideLoadingModal = () => {
        setLoadingModalShow(false);
        setLoadingModalTitle("");
    };

    const contextValue: GlobalModalContextType = useMemo(
        () => ({
            showModal,
            setModalShow,
            showLoadingModal,
            hideLoadingModal,
            isModalVisible: modalShow,
            isLoadingModalVisible: loadingModalShow,
        }),
        [modalShow, loadingModalShow]
    );

    return (
        <GlobalModalContext.Provider value={contextValue}>
            {children}
            <DefaultModal {...modalProps} modalShow={modalShow} />
            <LoadingModal
                showModal={loadingModalShow}
                title={loadingModalTitle}
            />
        </GlobalModalContext.Provider>
    );
};

export const useGlobalModal = (): GlobalModalContextType => {
    const context = useContext(GlobalModalContext);
    if (!context) {
        throw new Error(
            "useGlobalModal must be used within a GlobalModalProvider"
        );
    }

    return context;
};

export default GlobalModalContext;
